/**
 * MusicKit JS loader + lifecycle helper.
 *
 * Loads Apple's official MusicKit JS v3 SDK from the Apple CDN, configures
 * it with our developer JWT, and exposes a thin promise-based API so the
 * rest of the app doesn't need to deal with the global script + event handshake.
 *
 * Why we built our own layer rather than calling MusicKit directly:
 *   - The SDK exposes itself globally as `window.MusicKit` only after the
 *     async script loads + fires `musickitloaded`. Code that imports a hook
 *     too early will see `undefined` and crash. This loader resolves once
 *     the SDK is genuinely ready.
 *   - The configure() call is one-shot per page. Wrapping it in a singleton
 *     promise prevents double-init when multiple components mount.
 *
 * Background-playback story:
 *   MusicKit plays through a SAME-ORIGIN <audio> element, so iOS / Android
 *   give us the same OS audio focus they grant Spotify and Apple Music.
 *   Screen-lock, app-switch, lock-screen controls — all "just work" once a
 *   user signs in with their Apple Music subscription.
 */

const SDK_URL = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let sdkPromise = null;       // resolves to `window.MusicKit`
let musicInstance = null;    // the configured singleton


function loadScript() {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('window is undefined'));
      return;
    }
    if (window.MusicKit) {
      resolve(window.MusicKit);
      return;
    }
    // MusicKit v3 fires `musickitloaded` once the global is ready.
    const onLoaded = () => {
      document.removeEventListener('musickitloaded', onLoaded);
      if (window.MusicKit) resolve(window.MusicKit);
      else reject(new Error('MusicKit global missing after load'));
    };
    document.addEventListener('musickitloaded', onLoaded);
    const tag = document.createElement('script');
    tag.src = SDK_URL;
    tag.async = true;
    tag.crossOrigin = 'anonymous';
    tag.onerror = () => {
      document.removeEventListener('musickitloaded', onLoaded);
      reject(new Error('failed to load musickit.js'));
    };
    document.head.appendChild(tag);
  });
  return sdkPromise;
}

async function fetchDeveloperToken() {
  // Frontend-side cache. MusicKit JS namespaces its stored Music User Token
  // by hashing the developer JWT — so if we hand it a different JWT on every
  // page load (which the backend does on pod restarts), the Music User Token
  // becomes effectively orphaned and the user appears as "Not Connected"
  // every reload. We pin a single JWT in localStorage and reuse it until
  // it's near expiry, so MusicKit's storage namespace stays stable.
  const CACHE_KEY = 'spin.appleMusic.devToken';
  const RENEW_SAFETY_DAYS = 7; // re-fetch when token is within 7 days of expiry

  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.token && parsed?.exp) {
        const safeExp = Number(parsed.exp) - RENEW_SAFETY_DAYS * 86400;
        if (Math.floor(Date.now() / 1000) < safeExp) {
          return parsed.token;
        }
      }
    }
  } catch { /* ignore parse / storage errors */ }

  const res = await fetch(`${API}/apple-music/developer-token`, { credentials: 'include' });
  if (!res.ok) throw new Error(`developer-token ${res.status}`);
  const body = await res.json();
  if (!body?.token) throw new Error('no token in response');

  try {
    // Decode the JWT's `exp` claim without verifying (we just need the timestamp).
    const payload = JSON.parse(atob(body.token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ token: body.token, exp: payload?.exp || 0 }));
  } catch { /* storage failed (private mode etc.) — that's fine, just no caching */ }

  return body.token;
}

/**
 * Returns the configured MusicKit instance, lazily creating it on the first
 * call. Safe to call repeatedly.
 */
export async function getMusicKit() {
  if (musicInstance) return musicInstance;
  const MK = await loadScript();
  // One-time cleanup: any prior MusicKit user tokens were minted under the
  // hardcoded India storefront and are now invalid because we configure
  // without `storefrontId`. Wipe them once so the user lands on a clean
  // "Not Connected" state and re-CONNECT mints a token in their own
  // storefront. The flag prevents this from running on every reload.
  try {
    const FIX_FLAG = 'spin.appleMusic.storefrontFixApplied';
    if (typeof window !== 'undefined' && !window.localStorage.getItem(FIX_FLAG)) {
      Object.keys(window.localStorage)
        .filter((k) => k.startsWith('music.') || k === 'spin.appleMusic.devToken')
        .forEach((k) => window.localStorage.removeItem(k));
      window.localStorage.setItem(FIX_FLAG, '1');
    }
  } catch { /* private mode etc. */ }

  const token = await fetchDeveloperToken();
  await MK.configure({
    developerToken: token,
    app: {
      name: 'MusicBox',
      build: '1.0.0',
    },
    // Don't hardcode storefrontId — let MusicKit pick it up from the user's
    // Apple Music account after authorize(). Hardcoding it to 'in' caused
    // AUTHORIZATION_ERROR for any user whose subscription is in a different
    // storefront (US, UK, etc.): the token was technically valid but invalid
    // FOR the India storefront we asked it to validate against, so MusicKit
    // wiped the live `musicUserToken` and prepare() failed with the
    // misleading "missing MusicUserToken" error.
    suppressErrorDialog: true,
  });
  musicInstance = MK.getInstance();

  // Feb 14 2026 — SDK-level explicit-content unblock.
  //
  // Field bug (Cafe Strokes, host WhatsApp): venue account had
  // "Allow Explicit Content" ON in Apple Music settings, yet the
  // system kept looping / repeating on tracks that were flagged
  // explicit. Trace: MusicKit JS v3 carries its OWN
  // `restrictExplicitContent` flag on the singleton, defaulted to
  // `true` on many builds regardless of the account's server-side
  // setting. When the SDK's flag is true, `setQueue` on an explicit
  // track silently drops the item / triggers a `mediaCanNotBeLoaded`
  // event, which our recovery paths misread as a stall → repeat loop.
  //
  // Force the SDK-level flag OFF so the ACCOUNT setting is the sole
  // gate. If a host actually wants explicit blocked, they toggle it
  // in Apple Music itself and Apple will still filter server-side.
  // Ref: developer.apple.com/documentation/musickitjs → 'Music.restrictExplicitContent'
  //
  // Fires AGAIN in signInToAppleMusic() because MusicKit v3 lazy-
  // defines these properties on the AUTHORIZED singleton — they
  // don't exist yet on the unauthorized-configure-only instance
  // (verified via runtime probe Feb 14 2026: `'restrictExplicitContent'
  //  in unauthorizedInstance === false`).
  unblockExplicitContent(musicInstance);
  return musicInstance;
}

/**
 * Force-clear the SDK-level explicit-content block on a MusicKit
 * instance. Safe to call multiple times, safe on builds that don't
 * expose either flag. See the field-bug comment in getMusicKit() —
 * this must fire again post-authorize() because MusicKit JS v3
 * lazy-defines these properties on the AUTHORIZED singleton (they
 * don't exist yet on the unauthorized-configure-only instance).
 */
export function unblockExplicitContent(instance) {
  if (!instance) return;
  try {
    // Assign unconditionally — property may exist only after authorize().
    instance.restrictExplicitContent = false;
    instance.explicitContentAllowed = true;
  } catch (e) {
    console.warn('[musicKitLoader] failed to unblock explicit content:', e?.message);
  }
}

/**
 * Prompt the user to sign into their Apple Music account. Returns the Music
 * User Token on success. Browsers will pop Apple's auth modal — MUST be
 * called from inside a user-gesture (button click), like any popup.
 */
export async function signInToAppleMusic() {
  const music = await getMusicKit();
  const userToken = await music.authorize();
  // Force-clear the SDK-level explicit block again — after authorize()
  // MusicKit re-syncs the user's account settings and can silently
  // re-enable `restrictExplicitContent`. Feb 14 2026 — Cafe Strokes.
  unblockExplicitContent(music);
  // Persist the user's actual Apple Music storefront to the backend so all
  // subsequent catalog searches resolve track IDs in their own region —
  // a non-IN user playing an IN-storefront playlist needs the backend to
  // remap via search, and the backend can only do that if it knows the
  // user's storefront.
  try {
    const sf = String(music?.storefrontCountryCode || music?.storefrontId || '').toLowerCase();
    if (sf && sf.length === 2) {
      await fetch(`${API}/apple-music/storefront`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storefront: sf }),
      }).catch(() => null);
    }
  } catch { /* best effort */ }
  return userToken; // also persisted by the SDK on `window.localStorage`
}

export async function signOutFromAppleMusic() {
  if (!musicInstance) return;
  await musicInstance.unauthorize();
  try { window.localStorage.removeItem('spin.appleMusic.devToken'); } catch { /* noop */ }
}

/**
 * Nuclear option for a wedged Apple MusicKit instance.
 *
 * Added Feb 12 2026 after Yazu Bangalore forensics showed the
 * `silent_audio_position_frozen` failure mode surviving BOTH soft
 * recoveries (seek+play nudge) AND the queue-rebuild escalation
 * (handleTrackEnded). Symptom: MusicKit reports `playbackState === 2`
 * but the internal `<audio>` element is disconnected/orphaned — no
 * amount of `.play()` or queue manipulation resurrects audible output
 * because the audio graph itself is dead. Only rebuilding the entire
 * MusicKit instance fixes it.
 *
 * What we do:
 *   1. `stop()` the current instance so it releases audio focus + drops
 *      any live MSE / EME sessions cleanly.
 *   2. Best-effort `unauthorize()` skip — we WANT to preserve the stored
 *      MusicUserToken so the fresh instance auto-reconnects without a
 *      user gesture. MusicKit stores it in localStorage under `music.*`
 *      and `getMusicKit()` will pick it up on next configure().
 *   3. Remove MusicKit's live `<audio>` elements from the DOM. They're
 *      what actually hold the wedged decoder — leaving them means the
 *      new instance sometimes shares the broken decoder.
 *   4. Null out the singletons so the very next `getMusicKit()` call
 *      re-runs `MK.configure()` + `MK.getInstance()`.
 *
 * Returns the freshly configured instance (via getMusicKit) — caller
 * MUST then re-attach event listeners and reload the queue (see the
 * silent-audio watchdog in MusicContext for the full recovery choreography).
 */
export async function hardResetMusicKit() {
  const wasAuthorized = !!(musicInstance && musicInstance.isAuthorized);
  try { musicInstance?.stop?.(); } catch { /* noop */ }
  // Kill any live `<audio>` element MusicKit spawned — that's the wedged
  // decoder that survives even `unauthorize()`.
  try {
    if (typeof document !== 'undefined') {
      document.querySelectorAll('audio').forEach((el) => {
        // Only touch elements MusicKit owns — its audio tags have no `src`
        // attribute we set, they're managed by the SDK. Our own silent
        // anchor sets an explicit `src` beginning with `data:` or `blob:`.
        try {
          const src = el.getAttribute('src') || '';
          if (src.startsWith('data:') || src.startsWith('blob:')) return; // ours
          try { el.pause(); } catch { /* noop */ }
          try { el.removeAttribute('src'); el.load(); } catch { /* noop */ }
          try { el.parentNode && el.parentNode.removeChild(el); } catch { /* noop */ }
        } catch { /* noop */ }
      });
    }
  } catch { /* noop */ }
  // Drop the singletons — next getMusicKit() reconfigures from scratch.
  musicInstance = null;
  sdkPromise = null; // force re-load of the SDK script too, cheap on 2nd load (CDN cached)
  // Rebuild. The stored MusicUserToken in localStorage will let the fresh
  // instance auto-reconnect to Apple Music without a user gesture — the
  // whole point of preserving auth through the reset.
  const fresh = await getMusicKit();
  return { instance: fresh, wasAuthorized };
}

export function isAppleMusicAuthorized() {
  if (!musicInstance) return false;
  // `isAuthorized` is a cached flag that can lie (says true after the
  // MusicUserToken has been wiped). Verify against the actual token —
  // either MusicKit's live property OR our localStorage fallback — so the
  // UI never claims "Connected" without a usable token.
  if (!musicInstance.isAuthorized) return false;
  const liveToken = musicInstance.musicUserToken;
  if (liveToken && String(liveToken).length > 10) return true;
  return !!getStoredUserToken();
}

/**
 * Forcibly resets MusicKit's auth state when it's lying ("isAuthorized=true
 * but no real token"). Returns true if it had to do anything.
 */
export async function reconcileAuthIfLying() {
  if (!musicInstance) return false;
  if (!musicInstance.isAuthorized) return false;
  const live = musicInstance.musicUserToken;
  const stored = getStoredUserToken();
  if ((live && String(live).length > 10) || stored) return false;
  // MusicKit thinks it's authorized but has no token. Unauthorize so the
  // app's UI flips to "Not connected" and the user can re-CONNECT cleanly.
  try { await musicInstance.unauthorize(); } catch { /* noop */ }
  return true;
}

export function getStoredUserToken() {
  if (typeof window === 'undefined') return null;
  // MusicKit stores it under a predictable key.
  try {
    const keys = Object.keys(window.localStorage);
    const k = keys.find((x) => x.startsWith('music.') && x.endsWith('.u'));
    return k ? window.localStorage.getItem(k) : null;
  } catch {
    return null;
  }
}
